// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { FreebetsIcon } from '../../../assets/img/betting';
import Primary from '../../../components/PrimaryButton';
import SpinePlayer from '../../../components/SpinePlayer';
import Formatter from '../../../i18n/formatter';
import { __, getCurrency } from '../../../i18n/l10n';
import theme from '../../../styled-components/dark';
import { MPSpineAnimationsIds } from '../../../types/animations';
import { type Licence } from '../../../types/common';
import { SoundIds } from '../../../types/sounds';
import { MPNotificationsTypes, type MPUserNotification } from '../types/user';

import MPViewWrapper from './common/MPViewWrapper';

const APPEARANCE_ANIMATION_DURATION = 1000;

const StyledMPViewWrapper = styled(MPViewWrapper)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
`;

const Buttons = styled.div`
  margin-top: auto;
  display: flex;
  position: relative;
  z-index: 2;

  & > button {
    flex-grow: 1;
    flex-basis: 0;

    &:last-child:not(:first-child) {
      margin-left: 1em;
    }
  }
`;

const commonButton = css`
  font-size: 1em;
  min-height: 2.6em;
`;

const SkipButton = styled(Primary)`
  ${commonButton};
  && {
    background-color: rgba(255, 255, 255, 0.5);
  }
`;

const NextButton = styled(Primary)`
  ${commonButton};
  && {
    background-color: #fff;
    color: #000;
  }
`;

const ClaimButton = styled(Primary)`
  ${commonButton};
  color: ${theme.BasketCtas.primary.color};
  background-color: ${theme.BasketCtas.primary.backgroundColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125em;
`;

const FreebetIcon = styled.img`
  width: 1.375em;
  height: 1.375em;
  display: inline-block;
  margin-left: 0.1em;
`;

const fadeInWithTranslationAnim = keyframes`
  0% {
    transform: translate(-50%, 25%);
    opacity: 0;
  }

  50% {
    transform: translate(-50%, 25%);
    opacity: 0;
  }

  100% {
    transform: translate(-50%, 0);
    opacity: 1;
  }
`;

const Infos = styled.div`
  color: #fff;
  font-weight: bold;
  text-align: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 60%;
  opacity: 0;

  animation: ${fadeInWithTranslationAnim} ${APPEARANCE_ANIMATION_DURATION}ms ease-in-out forwards;
  animation-delay: ${APPEARANCE_ANIMATION_DURATION}ms;
`;

const InfosLabel = styled.div`
  font-size: 1.5em;
  margin-bottom: 0.6em;
  white-space: nowrap;
`;

const InfosAmount = styled.div`
  background-color: rgba(10, 11, 15, 0.6);
  border-radius: 0.2em;
  font-size: 2.1em;
  padding: 0.1em 0.5em;
  align-items: center;
  display: inline-block;
`;

type MPNotificationProps = {
  className?: string;
  licence: Licence;
  notifications: MPUserNotification[];
  onClaimReward: (uuid: string) => void;
  onFlushNotifications: () => void;
  onSkipNotifications: () => void;
  onSkipCurrentNotification: () => void;
};

const MPNotification = ({
  className = '',
  licence,
  notifications,
  onClaimReward,
  onSkipNotifications,
  onSkipCurrentNotification,
}: MPNotificationProps) => {
  const notification = useMemo(() => notifications[0], [notifications]);

  const animationId = useMemo(() => {
    if (!notification) {
      return;
    }

    const { type, data } = notification;

    if (type && data) {
      const { cardIndex, familyId } = data;
      const isCard = type === MPNotificationsTypes.CARD_WON;

      if (familyId) {
        let id = null;

        if (cardIndex && isCard) {
          id = `MP_WIN_${licence}_CARD_${familyId}_${cardIndex}`;
        } else if (!isCard) {
          id = `MP_WIN_${licence}_FAMILY_${familyId}`;
        }

        return MPSpineAnimationsIds[id];
      }
    }
  }, [notification, licence]);

  const handleNextNotification = useCallback(() => {
    if (notifications.length) {
      onSkipCurrentNotification();
    }
  }, [notifications.length, onSkipCurrentNotification]);

  const handleSkipAllNotification = useCallback(() => {
    onSkipNotifications();
  }, [onSkipNotifications]);

  const handleClaimFreebet = useCallback(() => {
    onClaimReward(notification.uuid);
    onSkipCurrentNotification();
  }, [notification?.uuid, onClaimReward, onSkipCurrentNotification]);

  const cardButtons = useMemo(() => {
    if (notifications.length > 1) {
      return (
        <>
          <SkipButton onClick={handleSkipAllNotification}>{__('Skip all')}</SkipButton>
          <NextButton onClick={handleNextNotification}>{__('Next')}</NextButton>
        </>
      );
    }
    return <NextButton onClick={handleSkipAllNotification}>{__('Ok')}</NextButton>;
  }, [handleNextNotification, handleSkipAllNotification, notifications.length]);

  const soundId = useMemo(
    () =>
      notification?.type === MPNotificationsTypes.FAMILY_COMPLETED ? SoundIds.MP_WIN_FAMILY : SoundIds.MP_WIN_CARD,
    [notification?.type],
  );

  if (!notification) {
    return null;
  }

  return (
    <StyledMPViewWrapper className={className} noScroll>
      <SpinePlayer animationId={animationId} scale={0.5} soundId={soundId} hideMuteButton />
      {notification?.data.totalAmount ? (
        <Infos>
          <InfosLabel>{__('You won Freebets')}</InfosLabel>
          <InfosAmount>{Formatter.formatNumber(notification.data.totalAmount, getCurrency())}</InfosAmount>
        </Infos>
      ) : null}

      <Buttons>
        {notification?.type === MPNotificationsTypes.CARD_WON ? (
          cardButtons
        ) : (
          <ClaimButton onClick={handleClaimFreebet}>
            {__('Claim')}
            <FreebetIcon src={FreebetsIcon} alt="freebet icon" />
          </ClaimButton>
        )}
      </Buttons>
    </StyledMPViewWrapper>
  );
};

export default MPNotification;
